import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CreateGroupMemberSuccess } from 'app/library/group-member/actions';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { GroupMemberApiService } from '../../group-member/services/group-member.api-service';
import {
    AddGroups,
    CreateGroup,
    CreateGroupFailure,
    CreateGroupSuccess,
    DeleteGroup,
    DeleteGroupFailure,
    DeleteGroupSuccess,
    FetchGroup,
    FetchGroupFailure,
    FetchGroupSuccess,
    FetchGroups,
    FetchGroupsFailure,
    FetchGroupsSuccess,
    LeaveGroup,
    LeaveGroupFailure,
    LeaveGroupSuccess,
    SetGroup,
    SetGroups,
    UnsetGroup,
    UpdateGroup,
    UpdateGroupFailure,
    UpdateGroupFavourite,
    UpdateGroupFavouriteFailure,
    UpdateGroupFavouriteSuccess,
    UpdateGroupSuccess,
    UploadGroupImageFailure,
} from '../actions';
import { UploadGroupImage, UploadGroupImageSuccess } from '../actions/index';
import { GroupApiService } from '../service/group.api-service';
import { Group } from './group';
import { GroupStateModel } from './group.state-model';

@State<GroupStateModel>({
    name: 'group',
    defaults: new GroupStateModel(),
})
@Injectable()
export class GroupState extends EntityState<Group> {
    @Selector()
    static getEntity(state: GroupStateModel, id: string = state.item): Group {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: GroupStateModel): Group[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    constructor(private _groupApiService: GroupApiService, private _groupMemberApiService: GroupMemberApiService) {
        super(null, 0);
    }

    @Action(AddGroups)
    addEntities(ctx: StateContext<GroupStateModel>, action: AddGroups): void {
        this._addEntities(ctx, action);
    }

    @Action(SetGroup)
    setEntity(ctx: StateContext<GroupStateModel>, action: SetGroup): void {
        this._setEntity(ctx, action, AddGroups);
    }

    @Action(UnsetGroup)
    unsetEntity(ctx: StateContext<GroupStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateGroup)
    createEntity(ctx: StateContext<GroupStateModel>, action: CreateGroup): void {
        this._createEntity(ctx, action, CreateGroupSuccess, CreateGroupFailure, AddGroups, null, this._groupApiService.create(action.payload)).then(() => {});
    }

    @Action(UpdateGroup)
    updateEntity(ctx: StateContext<GroupStateModel>, action: UpdateGroup): void {
        this._updateEntity(ctx, action, UpdateGroupSuccess, UpdateGroupFailure, AddGroups, this._groupApiService.update(action.payload)).then(() => {});
    }

    @Action(UpdateGroupFavourite)
    updateGroup(ctx: StateContext<GroupStateModel>, action: UpdateGroupFavourite): void {
        this._groupMemberApiService.updateFavouriteFlag(action.payload).subscribe(
            (entity) => {
                ctx.dispatch([new AddGroups([entity.group]), new UpdateGroupFavouriteSuccess(entity.group)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UpdateGroupFavouriteFailure());
            }
        );
    }

    @Action(DeleteGroup)
    deleteEntity(ctx: StateContext<GroupStateModel>, action: DeleteGroup): void {
        this._groupApiService.delete(action.payload.id, action.payload.notify).subscribe(
            () => {
                ctx.dispatch(new DeleteGroupSuccess(action.payload.id));
            },
            (error) => {
                ctx.dispatch(new DeleteGroupFailure());
            }
        );
    }

    @Action(FetchGroup)
    fetchEntity(ctx: StateContext<GroupStateModel>, action: FetchGroup): void {
        this._fetchEntity(ctx, action, FetchGroupSuccess, FetchGroupFailure, AddGroups, SetGroup, this._groupApiService.read(action.payload)).then(() => {});
    }

    @Action(FetchGroupSuccess)
    fetchEntitySuccess(ctx: StateContext<GroupStateModel>, action: FetchGroupSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchGroupFailure)
    fetchEntityFailure(ctx: StateContext<GroupStateModel>, action: FetchGroupFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetGroups)
    setEntities(ctx: StateContext<GroupStateModel>, action: SetGroups): void {
        throw new Error('Method not implemented.');
    }

    @Action(FetchGroups)
    fetchEntities(ctx: StateContext<GroupStateModel>): void {
        throw new Error('Method not implemented.');
    }

    @Action(FetchGroupsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<GroupStateModel>): void {
        throw new Error('Method not implemented.');
    }

    @Action(FetchGroupsFailure)
    fetchEntitiesFailure(ctx: StateContext<GroupStateModel>): void {
        throw new Error('Method not implemented.');
    }

    @Action(UploadGroupImage)
    uploadGroupImage(ctx: StateContext<GroupStateModel>, action: UploadGroupImage): void {
        this._groupApiService.uploadImage(action.payload).subscribe(
            (item) => {
                ctx.dispatch(new UploadGroupImageSuccess(item));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UploadGroupImageFailure());
            }
        );
    }

    @Action(LeaveGroup)
    leaveGroup(ctx: StateContext<GroupStateModel>, action: LeaveGroup): void {
        this._groupApiService.leaveGroup(action.payload).subscribe(
            () => {
                ctx.dispatch(new LeaveGroupSuccess(action.payload));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new LeaveGroupFailure());
            }
        );
    }

    @Action(CreateGroupMemberSuccess)
    createGroupMemberSuccess(ctx: StateContext<GroupStateModel>, action: CreateGroupMemberSuccess): void {
        action.payload.forEach((entity) => {
            ctx.dispatch(new SetGroup(entity.group));
        });
    }
}
